export type OhisToothKey = "16" | "11" | "26" | "36" | "31" | "46";

export interface OhisTooth {
  tooth_used: string; // gigi yang akhirnya dinilai (default = key; bisa diganti alternatif mis. 17 saat 16 hilang)
  examined: boolean;
  debris: number | null;   // 0-3, null jika belum diisi
  calculus: number | null; // 0-3
  alasan_excluded?: string | null;
}

export type OhisData = Record<OhisToothKey, OhisTooth>;

export type FotoKategori = "intraoral" | "ekstraoral" | "perawatan" | "lainnya";

export interface FotoKlinis {
  id: number;
  kategori: FotoKategori;
  view: string | null;
  original_name: string | null;
  mime: string | null;
  size: number | null;
  catatan: string | null;
  taken_at: string | null;
  url: string | null;
  uploaded_by: number | null;
  created_at: string | null;
}

export type Interpretasi = "baik" | "sedang" | "buruk" | null;

export interface AsesmenDentalKhusus {
  id: number | null;
  register_id: string;
  pasien_rm: number | string;
  tgl_periksa: string;
  ohis: OhisData;
  di_s: number | null;
  ci_s: number | null;
  ohis_total: number | null;
  interpretasi: Interpretasi;
  catatan: string | null;
  dokter: { id: number; nama: string } | null;
  user: { id: number; nama: string } | null;
  fotos: FotoKlinis[];
  link_pdf: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const OHIS_SENTINEL_TEETH: OhisToothKey[] = ["16", "11", "26", "36", "31", "46"];

export const OHIS_TOOTH_LABEL: Record<OhisToothKey, { surface: string; description: string }> = {
  "16": { surface: "Bukal", description: "Molar kanan atas" },
  "11": { surface: "Labial", description: "Insisivus kanan atas" },
  "26": { surface: "Bukal", description: "Molar kiri atas" },
  "36": { surface: "Lingual", description: "Molar kiri bawah" },
  "31": { surface: "Labial", description: "Insisivus kiri bawah" },
  "46": { surface: "Lingual", description: "Molar kanan bawah" },
};

// Alternatif jika gigi indeks tidak dapat dinilai (WHO Oral Health Surveys)
export const OHIS_ALT: Record<OhisToothKey, string[]> = {
  "16": ["17", "18"],
  "11": ["21"],
  "26": ["27", "28"],
  "36": ["37", "38"],
  "31": ["41"],
  "46": ["47", "48"],
};

export const INTRAORAL_VIEWS = [
  { value: "frontal_occlusion", label: "Frontal Occlusion" },
  { value: "right_buccal", label: "Right Buccal Occlusion" },
  { value: "left_buccal", label: "Left Buccal Occlusion" },
  { value: "maxillary_occlusal", label: "Maxillary Occlusal" },
  { value: "mandibular_occlusal", label: "Mandibular Occlusal" },
];

export const EKSTRAORAL_VIEWS = [
  { value: "frontal_rest", label: "Frontal at Rest" },
  { value: "frontal_smile", label: "Frontal Smiling" },
  { value: "profile_left", label: "Profile Left" },
  { value: "profile_right", label: "Profile Right" },
  { value: "three_quarter", label: "3/4 View" },
];

// Foto sebelum & sesudah perawatan (dental treatment) — RM.RJ.GIGI.DK.01
export const PERAWATAN_VIEWS = [
  { value: "pre_perawatan", label: "Pre-Perawatan (Sebelum)" },
  { value: "post_perawatan", label: "Post-Perawatan (Sesudah)" },
];

export const LAINNYA_VIEWS = [
  { value: "pre_op", label: "Pre-Op" },
  { value: "post_op", label: "Post-Op" },
  { value: "follow_up", label: "Follow Up" },
  { value: "umum", label: "Umum" },
];

export function emptyOhisData(): OhisData {
  return OHIS_SENTINEL_TEETH.reduce((acc, k) => {
    acc[k] = { tooth_used: k, examined: true, debris: null, calculus: null, alasan_excluded: null };
    return acc;
  }, {} as OhisData);
}

export function recomputeOhis(ohis: OhisData) {
  let debrisSum = 0;
  let calculusSum = 0;
  let n = 0;
  for (const k of OHIS_SENTINEL_TEETH) {
    const t = ohis[k];
    if (!t || !t.examined) continue;
    if (t.debris == null || t.calculus == null) continue;
    debrisSum += Number(t.debris);
    calculusSum += Number(t.calculus);
    n++;
  }
  if (n === 0) {
    return { di_s: null, ci_s: null, ohis_total: null, interpretasi: null as Interpretasi };
  }
  const diS = Math.round((debrisSum / n) * 100) / 100;
  const ciS = Math.round((calculusSum / n) * 100) / 100;
  const total = Math.round((diS + ciS) * 100) / 100;
  let interp: Interpretasi;
  if (total <= 1.2) interp = "baik";
  else if (total <= 3.0) interp = "sedang";
  else interp = "buruk";
  return { di_s: diS, ci_s: ciS, ohis_total: total, interpretasi: interp };
}
